import React, { useState, useEffect } from 'react';
import type { TeamMember, CreateTeamMemberRequest, UpdateTeamMemberRequest } from '../../services/timetablesService';

interface TeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateTeamMemberRequest | UpdateTeamMemberRequest) => void;
  member?: TeamMember;
}

const timezones = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Vancouver',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Seoul',
  'Asia/Hong_Kong',
  'Asia/Singapore',
  'Asia/Dubai',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Pacific/Auckland',
];

export const TeamMemberModal: React.FC<TeamMemberModalProps> = ({ isOpen, onClose, onSave, member }) => {
  const [formData, setFormData] = useState({
    name: '',
    timezone: 'America/New_York',
  });

  useEffect(() => {
    if (member) {
      setFormData({
        name: member.name,
        timezone: member.timezone,
      });
    } else {
      setFormData({
        name: '',
        timezone: 'America/New_York',
      });
    }
  }, [member, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div
      className='fixed inset-0 flex items-center justify-center z-50'
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        className='dashboard-card fade-in'
        style={{
          width: '90%',
          maxWidth: '480px',
          margin: '20px',
        }}
      >
        <div className='dashboard-card__header'>
          <h2 className='dashboard-card__title' style={{ margin: 0 }}>
            {member ? 'Edit Team Member' : 'Add Team Member'}
          </h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className='dashboard-card__content' style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: 'var(--white-text)',
                  marginBottom: '8px',
                }}
              >
                Name
              </label>
              <input
                type='text'
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid var(--divider-gray)`,
                  borderRadius: '4px',
                  backgroundColor: 'var(--tertiary-bg)',
                  color: 'var(--white-text)',
                  fontSize: '0.875rem',
                  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                }}
                placeholder='Enter team member name'
                onFocus={e => {
                  const input = e.currentTarget;
                  input.style.borderColor = 'var(--primary-red)';
                  input.style.boxShadow = '0 0 0 2px rgba(227, 27, 35, 0.2)';
                }}
                onBlur={e => {
                  const input = e.currentTarget;
                  input.style.borderColor = 'var(--divider-gray)';
                  input.style.boxShadow = 'none';
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: 'var(--white-text)',
                  marginBottom: '8px',
                }}
              >
                Timezone
              </label>
              <select
                value={formData.timezone}
                onChange={e => setFormData({ ...formData, timezone: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid var(--divider-gray)`,
                  borderRadius: '4px',
                  backgroundColor: 'var(--tertiary-bg)',
                  color: 'var(--white-text)',
                  fontSize: '0.875rem',
                  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                }}
                onFocus={e => {
                  const select = e.currentTarget;
                  select.style.borderColor = 'var(--primary-red)';
                  select.style.boxShadow = '0 0 0 2px rgba(227, 27, 35, 0.2)';
                }}
                onBlur={e => {
                  const select = e.currentTarget;
                  select.style.borderColor = 'var(--divider-gray)';
                  select.style.boxShadow = 'none';
                }}
              >
                {timezones.map(tz => (
                  <option key={tz} value={tz} style={{ backgroundColor: 'var(--tertiary-bg)', color: 'var(--white-text)' }}>
                    {tz}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className='dashboard-card__footer'>
            <div></div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type='button'
                onClick={onClose}
                style={{
                  padding: '8px 16px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: 'var(--light-gray)',
                  backgroundColor: 'var(--tertiary-bg)',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseOver={e => (e.currentTarget.style.backgroundColor = 'var(--divider-gray)')}
                onMouseOut={e => (e.currentTarget.style.backgroundColor = 'var(--tertiary-bg)')}
              >
                Cancel
              </button>
              <button
                type='submit'
                style={{
                  padding: '8px 16px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: 'var(--white-text)',
                  backgroundColor: 'var(--primary-red)',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseOver={e => (e.currentTarget.style.backgroundColor = 'var(--dark-red)')}
                onMouseOut={e => (e.currentTarget.style.backgroundColor = 'var(--primary-red)')}
              >
                {member ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
